import { supabase } from "../supabase";
import { sendNewMessageEmail } from "./emailService";
import { checkRateLimit } from '../utils/rateLimiter';

export async function getOrCreateConversation(participantAId, participantBId) {
    // Ensure deterministic order for uniqueness
    const [a, b] = participantAId < participantBId ? [participantAId, participantBId] : [participantBId, participantAId];

    // Look up exact ordered pair
    const { data: existing, error: existingError } = await supabase
        .from("conversations")
        .select("id")
        .eq("participant_a", a)
        .eq("participant_b", b)
        .maybeSingle();

    if (existingError && existingError.code !== "PGRST116") throw existingError;
    if (existing) return existing;

    // Create if not exists
    const { data: created, error: createError } = await supabase
        .from("conversations")
        .insert([{ participant_a: a, participant_b: b }])
        .select("id")
        .single();

    if (createError) throw createError;
    return created;
}

export async function listConversations(userId) {
    // Fetch conversations for user
    const { data, error } = await supabase
        .from("conversations")
        .select("id, participant_a, participant_b, last_message_at")
        .or(`participant_a.eq.${userId},participant_b.eq.${userId}`)
        .order("last_message_at", { ascending: false });
    if (error) throw error;
    return data;
}

export async function fetchMessages(conversationId, limit = 50) {
    const { data, error } = await supabase
        .from("messages")
        .select("id, sender_id, content, attachments, created_at")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true })
        .limit(limit);
    if (error) throw error;
    
    return data;
}

export async function sendMessage(conversationId, senderId, content, attachments = null) {
    // Rate limit: Max 50 messages per minute per user
    const rateLimitKey = `message_${senderId}`;
    if (!checkRateLimit(rateLimitKey, 50, 60000)) {
        throw new Error('Rate limit exceeded. Please wait a moment before sending another message.');
    }

    // Ensure attachments are properly formatted for JSONB storage
    let attachmentsData = null;
    if (attachments && Array.isArray(attachments) && attachments.length > 0) {
        attachmentsData = attachments;
    }
    
    const { data, error } = await supabase
        .from("messages")
        .insert([{ conversation_id: conversationId, sender_id: senderId, content, attachments: attachmentsData }])
        .select("id, sender_id, content, attachments, created_at")
        .single();
    
    if (error) {
        throw error;
    }
    
    // Note: Notification is now created automatically by database trigger
    // No need to create it manually here
    
    // Send email notification to recipient
    try {
        // Get conversation to find recipient
        const { data: conversation, error: convError } = await supabase
            .from("conversations")
            .select("participant_a, participant_b")
            .eq("id", conversationId)
            .single();
        
        if (!convError && conversation) {
            // Find recipient (the one who didn't send the message)
            const recipientId = conversation.participant_a === senderId 
                ? conversation.participant_b 
                : conversation.participant_a;
            
            // Get recipient's email and name
            const { data: recipientProfile } = await supabase
                .from("users_profiles")
                .select("profileid, email, firstname, lastname, companyname")
                .eq("profileid", recipientId)
                .single();
            
            // Get sender's name
            const { data: senderProfile } = await supabase
                .from("users_profiles")
                .select("profileid, firstname, lastname, companyname")
                .eq("profileid", senderId)
                .single();
            
            if (recipientProfile && recipientProfile.email && senderProfile) {
                const recipientName = `${recipientProfile.firstname || ''} ${recipientProfile.lastname || ''}`.trim() || recipientProfile.email.split('@')[0];
                const senderName = senderProfile.companyname || `${senderProfile.firstname || ''} ${senderProfile.lastname || ''}`.trim() || 'Someone';
                
                // Determine correct messages page route based on recipient's role
                const recipientRole = recipientProfile.role || 'Employee';
                const messagesRoute = recipientRole === 'Employer' 
                    ? '/employer/messages' 
                    : '/employee/messages';
                const messageLink = `https://doctorsjobs.io${messagesRoute}?conversation=${conversationId}`;
                
                // Send email (don't await - send in background)
                sendNewMessageEmail(
                    recipientProfile.email,
                    recipientName,
                    senderName,
                    messageLink,
                    1
                ).catch(() => {
                    // Don't fail message sending if email fails
                });
            }
        }
    } catch (emailError) {
        // Don't fail message sending if email fails
    }
    
    return data;
}

export function subscribeToMessages(conversationId, onInsert) {
    const channel = supabase
        .channel(`messages:${conversationId}`)
        .on(
            "postgres_changes",
            { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${conversationId}` },
            (payload) => {
                onInsert?.(payload.new);
            }
        )
        .subscribe();

    return () => {
        supabase.removeChannel(channel);
    };
}

export async function fetchProfilesByIds(userIds) {
    const unique = Array.from(new Set(userIds.filter(Boolean)));
    if (unique.length === 0) return [];
    const { data, error } = await supabase
        .from("users_profiles")
        .select("profileid, firstname, lastname, profile_image_url, companyname, role")
        .in("profileid", unique);
    if (error) throw error;
    return data || [];
}


